import { createSlice } from "@reduxjs/toolkit";

import {
  fetchActivityFeed,
  fetchAiUsage,
  fetchAiUsageAvailability,
  fetchByArea,
  fetchByUser,
  fetchTokenUsageTimeline,
  fetchVisiChatHistory,
  tokenUsageQueryKey,
} from "./actions";

export const initialState = {
  byArea: null,
  byUser: null,
  isLoading: false,
  error: null,
  filters: { range: "all", from: null, to: null, source: "all" },
  activityEvents: [],
  activityIsLoading: false,
  activityIsLoadingMore: false,
  activityError: null,
  activitySearch: "",
  activitySort: { field: "dateTime", direction: "DESC" },
  activityTotal: 0,
  activityFilteredTotal: 0,
  activityHasMore: false,
  visiChats: [],
  visiChatsTotal: 0,
  visiChatsIsLoading: false,
  visiChatsHasLoaded: false,
  visiChatsError: null,
  visiChatsRequestId: null,
  aiUsage: null,
  aiUsageIsLoading: false,
  aiUsageError: null,
  aiUsageRequestId: null,
  aiUsageUser: null,
  aiUsageUserOptions: [],
  aiUsageAvailability: null,
  aiUsageAvailabilityStatus: "idle",
  aiUsageAvailabilityChecked: false,
  tokenUsage: {
    period: { range: "last90", from: null, to: null },
    segments: { chart: null, table: null, personTable: null },
    detailsSource: "all",
    queries: {},
  },
};

const DEFAULT_ERROR = "Failed to load usage stats";
const ACTIVITY_DEFAULT_ERROR = "Failed to load activity";
const VISI_CHATS_DEFAULT_ERROR = "Failed to load Visi chats";
const AI_USAGE_DEFAULT_ERROR = "Failed to load AI usage";
const TOKEN_USAGE_DEFAULT_ERROR = "Failed to load token usage";

const queryFor = (state, action) =>
  state.tokenUsage.queries[
    tokenUsageQueryKey(action.meta.arg.period, action.meta.arg.granularity)
  ];

const usersFromAiUsage = (aiUsage, previousOptions) => {
  if (aiUsage.view?.scope === "WORKSPACE") {
    return (aiUsage.byUser ?? []).map(({ userName }) => userName);
  }
  if (aiUsage.view?.scope === "WORKSPACE_USER") {
    const selected = aiUsage.view.userName;
    return previousOptions.includes(selected)
      ? previousOptions
      : [...previousOptions, selected];
  }
  return [];
};

const usageStatsSlice = createSlice({
  name: "usageStats",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetError: (state) => {
      state.error = null;
      state.activityError = null;
    },
    setActivitySearch: (state, action) => {
      state.activitySearch = action.payload ?? "";
    },
    setActivitySort: (state, action) => {
      state.activitySort = action.payload;
    },
    setAiUsageUser: (state, action) => {
      state.aiUsageUser = action.payload || null;
    },
    setTokenUsagePeriod: (state, action) => {
      if (action.payload.range !== state.tokenUsage.period.range) {
        state.tokenUsage.segments.chart = null;
      }
      state.tokenUsage.period = { from: null, to: null, ...action.payload };
    },
    setTokenUsageSegment: (state, action) => {
      const { slot, segment } = action.payload;
      state.tokenUsage.segments[slot] = segment;
    },
    setTokenUsageDetailsSource: (state, action) => {
      state.tokenUsage.detailsSource = action.payload;
    },
    clearTokenUsageQueries: (state) => {
      state.tokenUsage.queries = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchByArea.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchByArea.fulfilled, (state, action) => {
        state.isLoading = false;
        state.byArea = action.payload;
      })
      .addCase(fetchByArea.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_ERROR;
      })
      .addCase(fetchByUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchByUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.byUser = action.payload;
      })
      .addCase(fetchByUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_ERROR;
      })
      .addCase(fetchActivityFeed.pending, (state, action) => {
        const isFirstPage = (action.meta.arg?.offset ?? 0) === 0;
        state.activityIsLoading = isFirstPage;
        state.activityIsLoadingMore = !isFirstPage;
        state.activityError = null;
      })
      .addCase(fetchActivityFeed.fulfilled, (state, action) => {
        const { offset, rows, total, filteredTotal } = action.payload;
        state.activityIsLoading = false;
        state.activityIsLoadingMore = false;
        state.activityEvents =
          offset === 0 ? rows : [...state.activityEvents, ...rows];
        state.activityTotal = total;
        state.activityFilteredTotal = filteredTotal;
        state.activityHasMore = state.activityEvents.length < filteredTotal;
      })
      .addCase(fetchActivityFeed.rejected, (state, action) => {
        state.activityIsLoading = false;
        state.activityIsLoadingMore = false;
        state.activityError = action.error?.message ?? ACTIVITY_DEFAULT_ERROR;
      })
      .addCase(fetchVisiChatHistory.pending, (state, action) => {
        state.visiChatsIsLoading = true;
        state.visiChatsError = null;
        state.visiChatsRequestId = action.meta.requestId;
      })
      .addCase(fetchVisiChatHistory.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.visiChatsRequestId) {
          return;
        }
        state.visiChatsIsLoading = false;
        state.visiChatsHasLoaded = true;
        state.visiChats = action.payload.conversations;
        state.visiChatsTotal = action.payload.total;
      })
      .addCase(fetchVisiChatHistory.rejected, (state, action) => {
        if (action.meta.requestId !== state.visiChatsRequestId) {
          return;
        }
        state.visiChatsIsLoading = false;
        state.visiChats = [];
        state.visiChatsTotal = 0;
        state.visiChatsError =
          action.error?.message ?? VISI_CHATS_DEFAULT_ERROR;
      })
      .addCase(fetchAiUsageAvailability.pending, (state) => {
        state.aiUsageAvailabilityStatus = "pending";
      })
      .addCase(fetchAiUsageAvailability.fulfilled, (state, action) => {
        state.aiUsageAvailabilityStatus = "settled";
        state.aiUsageAvailabilityChecked = true;
        state.aiUsageAvailability = action.payload;
      })
      .addCase(fetchAiUsageAvailability.rejected, (state) => {
        state.aiUsageAvailabilityStatus = "settled";
        state.aiUsageAvailabilityChecked = true;
        state.aiUsageAvailability = null;
      })
      .addCase(fetchTokenUsageTimeline.pending, (state, action) => {
        const key = tokenUsageQueryKey(
          action.meta.arg.period,
          action.meta.arg.granularity
        );
        const previous = state.tokenUsage.queries[key];
        state.tokenUsage.queries[key] = {
          data: previous?.data ?? null,
          isLoading: true,
          error: null,
          requestId: action.meta.requestId,
        };
      })
      .addCase(fetchTokenUsageTimeline.fulfilled, (state, action) => {
        const query = queryFor(state, action);
        if (query?.requestId !== action.meta.requestId) {
          return;
        }
        query.isLoading = false;
        query.data = action.payload;
      })
      .addCase(fetchTokenUsageTimeline.rejected, (state, action) => {
        const query = queryFor(state, action);
        if (query?.requestId !== action.meta.requestId) {
          return;
        }
        query.isLoading = false;
        query.data = null;
        query.error =
          action.payload ?? action.error?.message ?? TOKEN_USAGE_DEFAULT_ERROR;
      })
      .addCase(fetchAiUsage.pending, (state, action) => {
        state.aiUsageIsLoading = true;
        state.aiUsageError = null;
        state.aiUsageRequestId = action.meta.requestId;
      })
      .addCase(fetchAiUsage.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.aiUsageRequestId) {
          return;
        }
        state.aiUsageIsLoading = false;
        state.aiUsage = action.payload;
        state.aiUsageUserOptions = usersFromAiUsage(
          action.payload,
          state.aiUsageUserOptions
        );
      })
      .addCase(fetchAiUsage.rejected, (state, action) => {
        if (action.meta.requestId !== state.aiUsageRequestId) {
          return;
        }
        state.aiUsageIsLoading = false;
        state.aiUsage = null;
        state.aiUsageError =
          action.payload ?? action.error?.message ?? AI_USAGE_DEFAULT_ERROR;
      });
  },
});

export const {
  resetError,
  setActivitySearch,
  setActivitySort,
  setAiUsageUser,
  setFilters,
  setTokenUsagePeriod,
  setTokenUsageSegment,
  setTokenUsageDetailsSource,
  clearTokenUsageQueries,
} = usageStatsSlice.actions;

export default usageStatsSlice.reducer;
